import { Injectable } from "@angular/core";
import { IAnthem, ICountry, IOfficialCommon } from "./countries-data/models";
import { AnthemsData, CountriesData, LanguagesData } from "./countries-data/data";
import { AngularFireDatabase } from "angularfire2/database";
import * as _ from "lodash";
import { Observable } from "rxjs/Observable";

@Injectable()
export class CountryService {
  constructor(
    private db: AngularFireDatabase,
    private _anthemsData: AnthemsData,
    private _countriesData: CountriesData,
    private _languagesData: LanguagesData) { }

  getCountryNativeName(countryCode: string): string {
    const values: Array<IOfficialCommon> = _.values(this.getCountryObjByCode(countryCode).name.native);
    if (values.length > 0) {
      return values[0].common;
    }

    return "";
  }

  getCountryCommonName(countryCode: string): string {
    return this.getCountryObjByCode(countryCode).name.common;
  }

  getCapital(countryCode: string): string {
    return this.getCountryObjByCode(countryCode).capital;
  }

  getContinents(countryCode: string): string {
    const continentObj = this.getCountryObjByCode(countryCode).geo.continent;

    return _.values(continentObj).join(", ");
  }

  getSubRegion(countryCode: string): string {
    return this.getCountryObjByCode(countryCode).geo.subregion;
  }

  getCallingCodePrefix(countryCode: string): string {
    const callingCode: Array<string> | Array<number> = this.getCountryObjByCode(countryCode).dialling.calling_code || [""];

    return callingCode.join(", ");
  }

  getArea(countryCode: string): number {
    return this.getCountryObjByCode(countryCode).geo.area;
  }

  getPopulationCount(countryCode: string): number {
    return this.getCountryObjByCode(countryCode).population.count;
  }

  getPopulationPrecentage(countryCode: string): number {
    return this.getCountryObjByCode(countryCode).population.worldPercentage;
  }

  getLanguages(countryCode: string): string {
    return _.values(this.getCountryObjByCode(countryCode).languages).join(", ");
  }

  getAnthemLink(countryCode: string): string {
    const anthemObj: IAnthem = this._anthemsData.getData()[countryCode];

    return anthemObj ? this._anthemsData.getData()[countryCode].link : undefined;
  }

  getWikiSuffix(countryCode: string): string {
    return this.getCountryObjByCode(countryCode).wikiLink;
  }

  getCountriesCodes(): Array<string> {
    return _.keys(this._countriesData.getData()).sort();
  }

  getFormattedCountries(): any {
    const countriesObj = {};
    const countries = _.values(this._countriesData.getData()).sort();
    for (const country of countries) {
      countriesObj[country.iso_3166_1_alpha2] = { code: country.iso_3166_1_alpha2, name: country.name.common };
    }

    return countriesObj;
  }

  getCountryObjByCode(countryCode: string): ICountry {
    return this._countriesData.getData()[countryCode.toUpperCase()];
  }

  save(countryCode: string, previousCountrycode: string): boolean {
    try {
      if (previousCountrycode) {
        if (previousCountrycode !== countryCode) {
          this.db.object(`/location/country/${previousCountrycode}`).remove().then(() => {
            const country = this.getCountryObjByCode(countryCode);
            this.reduceContryData(country).then(
              reducedCountryData => {
                this.db.list("/location/country").set(countryCode, reducedCountryData);
              }
            );
          });
        }
      } else {
        const country = this.getCountryObjByCode(countryCode);
        this.reduceContryData(country).then(
          reducedCountryData => {
            this.db.list("/location/country").set(countryCode, reducedCountryData);
          }
        );
      }

      return true;
    } catch (err) {
      return false;
    }
  }

  reduceContryData(country): Promise<any> {
    return new Promise<any>(resolve => {
      delete country["alt_spellings"];
      delete country["capital"];
      delete country["currency"];
      delete country["demonym"];
      delete country["dialling"];
      delete country["extra"];
      delete country["geo"];
      delete country["languages"];
      delete country["population"];
      delete country["tld"];
      delete country["translations"];
      delete country["wikiLink"];
      delete country["name"]["native"];

      resolve(country);
    });
  }

  getCountries(): Observable<Array<any>> {
    return this.db.list(`/location/country`)
      .valueChanges();
  }

  delete(country: ICountry): boolean {
    try {
      const countryCode = country.iso_3166_1_alpha2;
      this.db.object(`/location/country/${countryCode}`).remove();

      return true;
    } catch (e) {
      return false;
    }
  }

  getCodesOfCountriesInUse(): Observable<Array<string>> {
    const communities = this.db.list(`/communities`)
      .valueChanges();

    return communities.map(community => {
      return community.map(country => {
        const addressSnap = country["location"]["address"];

        if (addressSnap) {
          return addressSnap["countryCode"];
        }
      });
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/location/country.service.ts